import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface WorkspaceMember {
  id: string;
  user_id: string;
  email: string;
  role: string;
  status: string;
  joined_at: string;
  invited_by?: string;
  profile?: {
    company_name?: string;
    contact_email?: string;
  };
}

export interface WorkspaceInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  expires_at: string;
  invited_by: string;
}

export const useWorkspaceMembers = (workspaceId: string) => {
  return useQuery({
    queryKey: ['workspace-members', workspaceId],
    queryFn: async () => {
      if (!workspaceId) {
        return { members: [], invitations: [] };
      }
      
      console.log('🔍 Fetching workspace members for:', workspaceId);

      // Get workspace members with enhanced error handling
      const { data: membersData, error: membersError } = await supabase
        .from('workspace_members')
        .select('*')
        .eq('workspace_id', workspaceId);

      if (membersError) {
        console.error('❌ Members query failed:', membersError);
        throw new Error(`Failed to fetch workspace members: ${membersError.message}`);
      }

      // Get workspace invitations with enhanced error handling
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('workspace_invitations')
        .select('*')
        .eq('workspace_id', workspaceId)
        .in('status', ['pending', 'sent']);

      if (invitationsError) {
        console.error('❌ Invitations query failed:', invitationsError);
        throw new Error(`Failed to fetch workspace invitations: ${invitationsError.message}`);
      }

      const members: WorkspaceMember[] = (membersData || []).map(member => ({
        id: member.id,
        user_id: member.user_id,
        email: `user-${member.user_id.slice(0, 8)}@example.com`,
        role: member.role || 'member',
        status: member.status || 'active',
        joined_at: member.created_at,
        invited_by: member.invited_by,
      }));

      const invitations: WorkspaceInvitation[] = invitationsData || [];

      console.log('✅ Successfully fetched:', { membersCount: members.length, invitationsCount: invitations.length });
      return { members, invitations };
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 2,
    retry: (failureCount, error) => {
      // Don't retry on RLS policy errors
      if (error?.message?.includes('infinite recursion')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};